import { SendEmailFormDataType } from "organisms/SendEmailDialog/SendEmailDialog.types";
import { InvoiceRecordType } from "pages/Booking/Booking.types";
import { CustomerRecordType } from "pages/Customer/Customer.types";
import {
  EmailRecipientRecordType,
  ExternalEmailContentType,
  FileVisibilityFlags,
  GenerateDocumentPersistResponseType,
  GenerateDocumentResponseType,
  GenerateDocumentTaskResponseType,
  MoodleEnrolledUserRecordType,
  MoodleUserRecordType,
  ProfileRecordType,
  UndeliverableEmailRecordType,
  UserListDataRecordType,
  UserListRecordType,
} from "pages/SharedRecords.types";
import { BrevoFolder } from "pages/UserList/CreateNewBrevoListForm";
import queryString from "query-string";
import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  fetchUtils,
  FilterPayload,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  PaginationPayload,
  RaRecord,
  SortPayload,
  UpdateManyParams,
  UpdateParams,
  UpdateResult,
} from "react-admin";

import { getGranularityByPeriod } from "../atoms/Charts/Charts.helper";
import { BookingsOverviewRecordType } from "../molecules/Charts/BookingsOverviewChart/BookingsOverviewChart.types";
import { accessTokenKey } from "./authProvider";
import {
  BulkUpsertEventAttendancesType,
  ExtendedDataProvider,
  GenerateDocumentRequestFilter,
  ReportChartFilterType,
} from "./dataProvider.types";
import { ProfileType } from "./profileHelpers";
import { apiEndpoint } from "./settings";

type ListResponse<RecordType> = {
  results: RecordType[];
  count: number;
};

interface Options extends RequestInit {
  user?: {
    authenticated?: boolean;
    token?: string;
  };
}

const stringify = queryString.stringify;

const getPaginationQuery = (pagination: PaginationPayload) => {
  return {
    page: pagination.page,
    page_size: pagination.perPage,
  };
};

const getFilterQuery = (filter: FilterPayload) => {
  const { q: search, ...otherSearchParams } = filter;

  if (typeof search === "string") {
    return { ...otherSearchParams, search };
  } else {
    return otherSearchParams;
  }
};

const getOrderingQuery = (sort: SortPayload) => {
  const { field, order } = sort;
  return {
    ordering: `${order === "ASC" ? "" : "-"}${field}`,
  };
};

export const getReportApiData = async <ApiResponseType, ReturnType = ApiResponseType>(
  resource: string,
  filter?: ReportChartFilterType,
  dataMapper?: (data: ApiResponseType) => ReturnType,
): Promise<ReturnType> => {
  let queryParams = "";
  if (filter) {
    const granularity = getGranularityByPeriod(filter.period);
    const { eventContext: context, eventBase: event_base, ...restFilter } = filter;
    queryParams = `?${stringify({
      context,
      event_base,
      granularity,
      ...restFilter,
    })}`;
  }

  const url = `${apiEndpoint}/${resource}/${queryParams}`;
  const response = await httpClient(url);

  let data = response.json as ReturnType;
  if (dataMapper) {
    data = dataMapper(response.json as ApiResponseType);
  }

  return data;
};

const drfProvider = (apiUrl: string, httpClient = fetchUtils.fetchJson): ExtendedDataProvider => {
  const performGetOne = async <RecordType extends RaRecord = RaRecord>(url: string): Promise<RecordType> => {
    const res = await httpClient(url);
    return res.json as RecordType;
  };

  const performCreate = async <RecordType extends RaRecord = RaRecord>(
    url: string,
    params?: CreateParams,
  ): Promise<RecordType> => {
    let body;

    if (params) {
      body = JSON.stringify(params.data);
    }

    const res = await httpClient(url, { method: "POST", body });
    return res.json as RecordType;
  };

  const performGetListOfIds = async (
    resource: string,
    params: {
      filter: FilterPayload;
    },
  ) => {
    const query = {
      ...getFilterQuery(params.filter),
    };
    const url = `${apiUrl}/${resource}/?${stringify(query)}&only_ids=true`;

    const response = await httpClient(url);
    const result = response.json as [number];

    return result;
  };

  return {
    getList: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetListParams) => {
      const query = {
        ...getFilterQuery(params.filter as FilterPayload),
        ...(params.pagination && getPaginationQuery(params.pagination)),
        ...(params.sort && getOrderingQuery(params.sort)),
      };
      const url = `${apiUrl}/${resource}/?${stringify(query)}`;

      const response = await httpClient(url);
      const result = response.json as ListResponse<RecordType>;

      return { data: result.results, total: result.count };
    },

    getOne: async <RecordType extends RaRecord = RaRecord>(
      resource: string,
      params: GetOneParams<RaRecord>,
      expand?: string,
    ) => {
      const url = expand
        ? `${apiUrl}/${resource}/${params.id}/?expand=${expand}`
        : `${apiUrl}/${resource}/${params.id}/`;
      const data = await performGetOne(url);
      return { data } as GetOneResult<RecordType>;
    },

    getMany: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetManyParams) => {
      const urlFor = (id: Identifier) => `${apiUrl}/${resource}/${id}/`;
      const data = await Promise.all(params.ids.map((id: Identifier) => performGetOne(urlFor(id))));

      return { data } as GetManyResult<RecordType>;
    },

    getManyReference: async <RecordType extends RaRecord = RaRecord>(
      resource: string,
      params: GetManyReferenceParams,
    ) => {
      const query = {
        ...getFilterQuery(params.filter as FilterPayload),
        ...getPaginationQuery(params.pagination),
        ...getOrderingQuery(params.sort),
        [params.target]: params.id,
      };
      const url = `${apiUrl}/${resource}/?${stringify(query)}`;

      const response = await httpClient(url);
      const result = response.json as ListResponse<RecordType>;

      return { data: result.results, total: result.count };
    },

    update: async <RecordType extends RaRecord = RaRecord>(resource: string, params: UpdateParams<RaRecord>) => {
      const res = await httpClient(`${apiUrl}/${resource}/${params.id}/`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      });

      const data = res.json as RecordType;
      return { data };
    },

    updateMany: async <RecordType extends RaRecord = RaRecord>(
      resource: string,
      params: UpdateManyParams<RaRecord>,
    ) => {
      const responses = await Promise.all(
        params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}/`, {
            method: "PATCH",
            body: JSON.stringify(params.data),
          }),
        ),
      );

      const data = responses.map((res) => {
        const record = res.json as RecordType;
        return record.id;
      });

      return { data };
    },

    create: async <RecordType extends RaRecord = RaRecord>(resource: string, params: CreateParams<RaRecord>) => {
      const data = await performCreate(`${apiUrl}/${resource}/`, params);
      return { data } as CreateResult<RecordType>;
    },

    delete: async <RecordType extends RaRecord = RaRecord>(resource: string, params: DeleteParams<RaRecord>) => {
      let url = `${apiUrl}/${resource}/${params.id}/`;

      const meta = params.meta as { query?: Record<string, unknown> } | undefined;
      if (meta?.query) {
        url += `?${stringify(meta.query)}`;
      }

      await httpClient(url, { method: "DELETE" });
      return { data: params.previousData } as DeleteResult<RecordType>;
    },

    deleteMany: async (resource, params) => {
      const meta = params.meta as { query?: Record<string, unknown> } | undefined;

      await Promise.all(
        params.ids.map((id) => {
          let url = `${apiUrl}/${resource}/${id}/`;

          if (meta?.query) {
            url += `?${stringify(meta.query)}`;
          }

          return httpClient(url, {
            method: "DELETE",
          });
        }),
      );
      return { data: [] };
    },

    getEnrolledUsersFor: async (resource, params) => {
      const url = `${apiUrl}/${resource}/${params.id}/enrolled_users/`;
      const response = await httpClient(url);
      const result = response.json as ListResponse<MoodleEnrolledUserRecordType>;

      return { data: result.results, total: result.count };
    },

    upsertAndConnectMoodleUserFor: async (resource, params) => {
      const url = `${apiUrl}/${resource}/${params.id}/moodle_user/`;
      const data = await performCreate(url);
      return { data } as CreateResult<MoodleUserRecordType>;
    },

    getMoodleUserFor: async (resource, params) => {
      const url = `${apiUrl}/${resource}/${params.id}/moodle_user/`;
      const data = await performGetOne(url);
      return { data } as GetOneResult<MoodleUserRecordType>;
    },

    deleteMoodleUserFor: async (resource, params) => {
      const url = `${apiUrl}/${resource}/${params.id}/moodle_user/`;
      await httpClient(url, { method: "DELETE" });
      return { data: params.previousData } as DeleteResult<MoodleUserRecordType>;
    },

    getBookingsOverviewData: async (filter): Promise<{ data: BookingsOverviewRecordType[] }> => {
      const query = getFilterQuery(filter as FilterPayload);
      const url = `${apiUrl}/dashboard_bookings_overview/?${stringify(query)}`;
      const response = await httpClient(url);
      return { data: response.json as BookingsOverviewRecordType[] };
    },

    toggleInvoiceIsPaid: async (id: number) => {
      const url = `${apiUrl}/invoices/${id}/toggle_is_paid/`;
      const res = await httpClient(url, { method: "PUT" });
      const invoice = res.json as InvoiceRecordType;
      return { data: invoice } as UpdateResult<InvoiceRecordType>;
    },

    syncInvoiceWithExternalAccounting: async (id: number) => {
      const url = `${apiUrl}/invoices/${id}/sync_with_accounting/`;
      const res = await httpClient(url, { method: "PUT" });
      const invoice = res.json as InvoiceRecordType;
      return { data: invoice } as UpdateResult<InvoiceRecordType>;
    },

    archive: async <RecordType extends RaRecord = RaRecord>(resource: string, id: Identifier) => {
      const url = `${apiUrl}/${resource}/${id}/archive/`;
      const data = await performCreate(url);
      return { data } as CreateResult<RecordType>;
    },

    unarchive: async <RecordType extends RaRecord = RaRecord>(resource: string, id: Identifier) => {
      const url = `${apiUrl}/${resource}/${id}/unarchive/`;
      const data = await performCreate(url);
      return { data } as CreateResult<RecordType>;
    },

    cancelBooking: async (id: Identifier) => {
      const url = `${apiUrl}/bookings/${id}/cancel/`;
      const data = await performCreate(url);
      return { data };
    },

    merge: async <RecordType extends RaRecord = RaRecord>(
      resource: string,
      params: { id: Identifier; mergeWith: Identifier },
    ) => {
      const { id, mergeWith } = params;
      const url = `${apiUrl}/${resource}/${id}/merge/`;
      const data = await performCreate(url, { data: { with: mergeWith } });
      return { data } as CreateResult<RecordType>;
    },
    generateDocument: async (filter: GenerateDocumentRequestFilter) => {
      const url = `${apiUrl}/generate_document/`;
      const body = JSON.stringify(filter);
      const response = await httpClient(url, { method: "POST", body });
      return { data: response.json as GenerateDocumentResponseType };
    },

    generateDocumentTask: async (taskId: number) => {
      const url = `${apiUrl}/generate_document_task/${taskId}/`;
      const response = await httpClient(url);
      return { status: response.status, data: response.json as GenerateDocumentTaskResponseType };
    },

    persistGeneratedDocuments: async (
      generatedDocumentIds: number[],
      fileVisibilityFlags: Partial<FileVisibilityFlags>,
    ) => {
      const url = `${apiUrl}/persist_generated_documents/`;
      const data = { generated_documents: generatedDocumentIds, visibility_flags: fileVisibilityFlags };
      const body = JSON.stringify(data);
      const response = await httpClient(url, { method: "POST", body });
      return { status: response.status, data: response.json as GenerateDocumentPersistResponseType };
    },

    alterBookings: async (id: Identifier, data: CreateParams) => {
      const url = `${apiUrl}/events/${id}/alter_bookings/`;
      await performCreate(url, data);
      return { data: [] };
    },

    bulkUpsertEventAttendances: async (data: CreateParams<BulkUpsertEventAttendancesType>) => {
      const url = `${apiUrl}/event_attendances/bulk_upsert/`;
      await performCreate(url, data);
      return { data };
    },
    bulkCreateUserListData: async (data: { consent_method?: string; list_data: Partial<UserListDataRecordType>[] }) => {
      const url = `${apiUrl}/user_list_data/bulk_create/`;
      const body = JSON.stringify({ data });
      const response = await httpClient(url, { method: "POST", body });
      return { data: response.json as UserListDataRecordType[] };
    },
    addNewBrevoList: async (id: number, data: object) => {
      const url = `${apiUrl}/user_lists/${id}/add_new_brevo_list/`;
      const body = JSON.stringify(data);
      const response = await httpClient(url, { method: "PUT", body });
      return { data: response.json as UserListRecordType };
    },
    syncBrevoList: async (id: number) => {
      const url = `${apiUrl}/user_lists/${id}/sync_to_brevo/`;
      await httpClient(url, { method: "POST" });
      return { data: {} };
    },
    importBrevoContacts: async (id: number) => {
      const url = `${apiUrl}/user_lists/${id}/import_brevo_contacts/`;
      await httpClient(url, { method: "POST" });
      return { data: {} };
    },
    getBrevoFolders: async () => {
      const url = `${apiUrl}/user_lists/get_brevo_folders/`;
      const response = await httpClient(url, { method: "GET" });
      return { data: response.json as BrevoFolder[] };
    },

    getListOfIds: async (
      resource: string,
      params: {
        filter: FilterPayload;
      },
    ) => {
      return performGetListOfIds(resource, params);
    },
    getEmailPreview: async (data: SendEmailFormDataType) => {
      const url = `${apiUrl}/emails/preview/`;
      const result = await performCreate(url, { data });
      return result.data as string;
    },
    getExternalEmailContent: async (id: Identifier) => {
      const url = `${apiUrl}/emails/${id}/external_content/`;
      const data = await performGetOne(url);
      return { data } as GetOneResult<ExternalEmailContentType>;
    },
    getProfile: async () => {
      const url = `${apiUrl}/profile/`;
      const data = await performGetOne(url);
      return { data } as GetOneResult<ProfileRecordType>;
    },
    updateProfile: async (updateData: Partial<ProfileRecordType>) => {
      const url = `${apiUrl}/profile/`;
      const res = await httpClient(url, { method: "PUT", body: JSON.stringify(updateData) });
      const data = res.json as ProfileRecordType;
      return { data } as UpdateResult<ProfileRecordType>;
    },
    getCustomer: async () => {
      const url = `${apiUrl}/customer/`;
      const data = await performGetOne(url);
      return { data } as GetOneResult<CustomerRecordType>;
    },
    updateCustomer: async (updateData: Partial<CustomerRecordType>) => {
      const url = `${apiUrl}/customer/`;
      const res = await httpClient(url, { method: "PUT", body: JSON.stringify(updateData) });
      const data = res.json as CustomerRecordType;
      return { data } as UpdateResult<CustomerRecordType>;
    },
    resendConsentVerification: async (scope: string) => {
      const url = `${apiUrl}/resend_consent_verification/`;
      await httpClient(url, {
        method: "POST",
        body: JSON.stringify({
          scope,
        }),
      });
      return { data: {} };
    },
    resendEmail: async (id: Identifier, to: string) => {
      const url = `${apiUrl}/emails/${id}/resend/`;
      await httpClient(url, {
        method: "POST",
        body: JSON.stringify({
          to: to,
        }),
      });
      return { data: {} };
    },
    userListUnsubscribe: async (unsubscribeToken?: string) => {
      if (!unsubscribeToken) return Promise.reject(new Error("No unsubscribe token found"));

      const url = `${apiUrl}/user_lists/unsubscribe/${unsubscribeToken}`;
      const res = await httpClient(url);
      return res.json as Record<string, never>;
    },
    validateEmailRecipients: async (recipients: EmailRecipientRecordType[]) => {
      const url = `${apiUrl}/email_jobs/validate_recipients/`;
      const res = await httpClient(url, { method: "POST", body: JSON.stringify({ recipients }) });
      return res.json as { data: UndeliverableEmailRecordType[]; recipient_names: string[]; total_count: number };
    },
    duplicateFeedbackForm: async (id: Identifier, data: CreateParams) => {
      const url = `${apiUrl}/feedback_forms/${id}/duplicate/`;
      await performCreate(url, data);
      return { data: [] };
    },
    oAuthFinishConnect: async (code: string) => {
      const url = `${apiUrl}/oauth/finish/`;
      const res = await httpClient(url, { method: "POST", body: JSON.stringify({ code }) });
      return res.status;
    },
    oAuthDisconnect: async () => {
      const url = `${apiUrl}/oauth/disconnect/`;
      const res = await httpClient(url, { method: "POST" });
      return res.status;
    },
    fetchFailedAccountingTransferInvoicesCount: async () => {
      const params = { filter: { has_accounting_transfer_error: true } };
      const result = await performGetListOfIds("invoices", params);
      return result.length;
    },
    resetAccountingTransfer: async (ids: number[]) => {
      const url = `${apiUrl}/invoices/reset_accounting_transfer/`;
      const res = await httpClient(url, { method: "POST", body: JSON.stringify({ ids }) });
      return res.status;
    },
  };
};

export function httpClient(url: string, options: Options = {}) {
  const storedProfile = localStorage.getItem("profile");
  const acceptLanguage = storedProfile ? (JSON.parse(storedProfile) as ProfileType).language || "de" : "de";

  const requestHeaders = (options.headers ||
    new Headers({ Accept: "application/json", "Accept-Language": acceptLanguage })) as Headers;

  const accessToken = localStorage.getItem(accessTokenKey);
  if (accessToken) {
    requestHeaders.set("X-Authorization", `Bearer ${accessToken}`);
  }

  return fetchUtils.fetchJson(url, { ...options, headers: requestHeaders });
}

export default drfProvider(apiEndpoint, httpClient);
